// The authorised operating list for a day (OT_0021/0023).
//
// Everything theatre needs off one sheet: what is being done, to whom, by whom,
// for how long, what to watch for, and how many times the case has already
// moved. It is built for printing — the allergy and bio-hazard lines are the
// reason a paper list still exists.
//
// Driven off the BOOKING, not the case: this is "what is happening in a theatre
// today", and a case without a booking is not on it.

import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import moment from "moment";

import { IPD_COLORS, IpdDateField, IpdSectionCard } from "../../ipd/utils";
import { formatIstTime } from "../../../utils/istDateTime";
import { fetchFinalList } from "../api/otApi";
import OtScreen from "../components/OtScreen";
import { printFinalList } from "../utils/otPdf";
import { LifecycleChip, OtSelect, errorMessage } from "../components/common";
import { OT_BASE } from "../constants";

export default function OtFinalList() {
  const history = useHistory();
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [listedOnly, setListedOnly] = useState("1");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (day, only) => {
    setLoading(true);
    setError("");
    try {
      setData(await fetchFinalList({ date: day, listed_only: only }));
    } catch (err) {
      setError(errorMessage(err, "Could not load the final list"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(date, listedOnly);
  }, [load, date, listedOnly]);

  const cases = data?.cases || [];

  return (
    <OtScreen
      title="Published Operating Theatre Master List"
      description="Authorized surgical master list for the operating suite, formatted for clinical distribution and theatre display."
      badge={data?.count}
      loading={loading}
      error={error}
      onRetry={() => load(date, listedOnly)}
      actions={
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Box sx={{ width: 220 }}>
            <OtSelect
              value={listedOnly}
              onChange={(e) => setListedOnly(e.target.value)}
              options={[
                { value: "1", label: "Authorized / Listed Cases Only" },
                { value: "0", label: "All Scheduled Bookings (Including Provisional)" },
              ]}
            />
          </Box>
          <Box sx={{ width: 170 }}>
            <IpdDateField value={date} onChange={(iso) => iso && setDate(iso)} />
          </Box>
          <Button size="small" variant="contained" startIcon={<PrintIcon />} onClick={() => printFinalList(date, data)}>
            Print Master List
          </Button>
        </Box>
      }
    >
      <IpdSectionCard
        title={moment(date, "YYYY-MM-DD").format("dddd, D MMMM YYYY")}
        subtitle={
          data?.authorised_only
            ? "Displaying formally approved and published surgical cases."
            : "Displaying all scheduled bookings across suites, including pending authorizations."
        }
      >
        {cases.length === 0 ? (
          <Typography sx={{ color: IPD_COLORS.textSecondary }}>
            No surgical cases scheduled on the operating list for this date.
          </Typography>
        ) : (
          cases.map((c, i) => (
            <Box
              key={c.ot_case_id}
              sx={{
                py: 2,
                borderBottom: i === cases.length - 1 ? "none" : `1px solid ${IPD_COLORS.border}`,
              }}
            >
              <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
                <Box sx={{ minWidth: 92 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                    {formatIstTime(c.start_datetime)}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                    to {formatIstTime(c.end_datetime)}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                    {c.estimated_duration_min ? `est ${c.estimated_duration_min} min` : ""}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1, minWidth: 260 }}>
                  <Typography sx={{ fontSize: 14.5, fontWeight: 700 }}>
                    {c.patient_name} · {c.health_record_id || "MRN Pending"} · {c.age ?? "—"}
                    {c.gender ? `/${c.gender}` : ""}
                  </Typography>
                  <Typography sx={{ fontSize: 13.5 }}>
                    {c.procedure_name || "Procedure Unspecified"}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
                    {c.operating_room_name} · {c.primary_surgeon_name || "Surgeon Unassigned"} ·{" "}
                    {c.anaesthetist_name || "Anaesthesiologist Unassigned"} · {c.scheduling_type}
                    {c.ward_name ? ` · ${c.ward_name}` : ""}
                  </Typography>
                  {c.diagnosis && (
                    <Typography sx={{ fontSize: 12.5, mt: 0.5 }}>
                      <strong>Diagnosis:</strong> {c.diagnosis}
                    </Typography>
                  )}
                  {c.special_instructions && (
                    <Typography sx={{ fontSize: 12.5 }}>
                      <strong>Instructions:</strong> {c.special_instructions}
                    </Typography>
                  )}
                  {c.remarks && (
                    <Typography sx={{ fontSize: 12.5 }}>
                      <strong>Remarks:</strong> {c.remarks}
                    </Typography>
                  )}
                  {(c.team || []).length > 0 && (
                    <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, mt: 0.5 }}>
                      Team: {c.team.map((m) => `${m.attendee_type_name} ${m.member_name}`).join(", ")}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ minWidth: 190 }}>
                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 0.75 }}>
                    <LifecycleChip status={c.lifecycle_status} />
                    {c.reschedule_count > 0 && (
                      <Box
                        sx={{
                          px: 0.8,
                          py: 0.3,
                          borderRadius: 0.75,
                          backgroundColor: IPD_COLORS.warning.bg,
                          color: IPD_COLORS.warning.text,
                          fontSize: 10.5,
                          fontWeight: 700,
                        }}
                      >
                        MOVED {c.reschedule_count}×
                      </Box>
                    )}
                  </Box>
                  {c.known_allergies && (
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: IPD_COLORS.warning.text }}>
                      ALLERGY: {c.known_allergies}
                    </Typography>
                  )}
                  {c.biohazard && (
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: IPD_COLORS.error.text }}>
                      BIO-HAZARD{c.biohazard_note ? `: ${c.biohazard_note}` : ""}
                    </Typography>
                  )}
                  <Button size="small" onClick={() => history.push(`${OT_BASE}/case/${c.ot_case_id}`)}>
                    View Case Record
                  </Button>
                </Box>
              </Box>
          </Box>
        ))
      )}
      </IpdSectionCard>
    </OtScreen>
  );
}
